import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';

/**
 * Apple "Hello"-style provisioning screen.
 *
 * Shows while the backend auto-provisions all services (~5-10s).
 * Rotates through Thai status messages with a smooth crossfade animation.
 */

const PROVISIONING_MESSAGES = [
  'กำลังสร้างพื้นที่ทำงาน...',        // Setting up workspace
  'กำลังเตรียมระบบแชท...',            // Setting up multichat
  'กำลังเปิดร้านค้าออนไลน์...',       // Setting up webstore
  'กำลังเชื่อมต่อช่องทางขาย...',      // Setting up marketplace integration
  'กำลังตั้งค่าระบบ CRM...',          // Setting up CRM
  'กำลังเตรียม AI ผู้ช่วย...',        // Setting up AI assistant
  'กำลังสร้างหน้าเว็บไซต์...',        // Setting up website builder
  'กำลังเตรียมระบบรายงาน...',         // Setting up analytics
  'กำลังเปิดใช้งานทุกอย่าง...',       // Activating everything
  'เกือบเสร็จแล้ว...',               // Almost done
];

// Cycle interval — each message shows for 1.2s
const MESSAGE_INTERVAL_MS = 2800;

/**
 * Each arm of the TuesdayOS logo — 3 SVG elements (cap, bar, arrow).
 * Grouped by compass direction with the offset they fly in from.
 */
const ARMS: { dx: number; dy: number; elements: string[] }[] = [
  // N — flies down from top
  { dx: 0, dy: -1, elements: [
    '<path d="M190 16C190 14.2928 189.664 12.6023 189.01 11.0251C188.357 9.44788 187.4 8.01477 186.192 6.80761C184.985 5.60045 183.552 4.64288 181.975 3.98957C180.398 3.33625 178.707 3 177 3C175.293 3 173.602 3.33626 172.025 3.98957C170.448 4.64288 169.015 5.60045 167.808 6.80761C166.6 8.01477 165.643 9.44788 164.99 11.0251C164.336 12.6023 164 14.2928 164 16L177 16H190Z" fill="#0051FD"/>',
    '<rect x="164" y="16" width="26" height="120" fill="#0051FD"/>',
    '<path d="M177 147L164 136L190 136L177 147Z" fill="#0051FD"/>',
  ]},
  // S — flies up from bottom
  { dx: 0, dy: 1, elements: [
    '<path d="M164 338C164 339.707 164.336 341.398 164.99 342.975C165.643 344.552 166.6 345.985 167.808 347.192C169.015 348.4 170.448 349.357 172.025 350.01C173.602 350.664 175.293 351 177 351C178.707 351 180.398 350.664 181.975 350.01C183.552 349.357 184.985 348.4 186.192 347.192C187.4 345.985 188.357 344.552 189.01 342.975C189.664 341.398 190 339.707 190 338L177 338L164 338Z" fill="#0051FD"/>',
    '<rect x="190" y="338" width="26" height="120" transform="rotate(180 190 338)" fill="#0051FD"/>',
    '<path d="M177 207L190 218L164 218L177 207Z" fill="#0051FD"/>',
  ]},
  // E — flies in from right
  { dx: 1, dy: 0, elements: [
    '<path d="M338 190C339.707 190 341.398 189.664 342.975 189.01C344.552 188.357 345.985 187.4 347.192 186.192C348.4 184.985 349.357 183.552 350.01 181.975C350.664 180.398 351 178.707 351 177C351 175.293 350.664 173.602 350.01 172.025C349.357 170.448 348.4 169.015 347.192 167.808C345.985 166.6 344.552 165.643 342.975 164.99C341.398 164.336 339.707 164 338 164L338 177L338 190Z" fill="#0051FD"/>',
    '<rect x="338" y="164" width="26" height="120" transform="rotate(90 338 164)" fill="#0051FD"/>',
    '<path d="M207 177L218 164L218 190L207 177Z" fill="#0051FD"/>',
  ]},
  // W — flies in from left
  { dx: -1, dy: 0, elements: [
    '<path d="M16 164C14.2928 164 12.6023 164.336 11.0251 164.99C9.44788 165.643 8.01477 166.6 6.80761 167.808C5.60045 169.015 4.64288 170.448 3.98957 172.025C3.33625 173.602 3 175.293 3 177C3 178.707 3.33625 180.398 3.98957 181.975C4.64288 183.552 5.60045 184.985 6.80761 186.192C8.01477 187.4 9.44788 188.357 11.0251 189.01C12.6023 189.664 14.2928 190 16 190L16 177L16 164Z" fill="#0051FD"/>',
    '<rect x="16" y="190" width="26" height="120" transform="rotate(-90 16 190)" fill="#0051FD"/>',
    '<path d="M147 177L136 190L136 164L147 177Z" fill="#0051FD"/>',
  ]},
  // SE — flies in from bottom-right
  { dx: 0.707, dy: 0.707, elements: [
    '<path d="M282.302 300.071C283.509 301.278 284.942 302.236 286.519 302.889C288.096 303.542 289.787 303.879 291.494 303.879C293.201 303.879 294.892 303.542 296.469 302.889C298.046 302.236 299.479 301.278 300.687 300.071C301.894 298.864 302.851 297.431 303.505 295.854C304.158 294.276 304.494 292.586 304.494 290.879C304.494 289.171 304.158 287.481 303.505 285.904C302.851 284.327 301.894 282.893 300.687 281.686L291.494 290.879L282.302 300.071Z" fill="#0051FD"/>',
    '<rect x="300.686" y="281.686" width="26" height="120" transform="rotate(135 300.686 281.686)" fill="#0051FD"/>',
    '<path d="M198.862 198.248L215.833 196.833L197.448 215.218L198.862 198.248Z" fill="#0051FD"/>',
  ]},
  // NW — flies in from top-left
  { dx: -0.707, dy: -0.707, elements: [
    '<path d="M72.999 53.9978C71.7919 52.7906 70.3588 51.8331 68.7815 51.1798C67.2043 50.5264 65.5138 50.1902 63.8066 50.1902C62.0995 50.1902 60.409 50.5264 58.8318 51.1798C57.2545 51.8331 55.8214 52.7906 54.6143 53.9978C53.4071 55.205 52.4495 56.6381 51.7962 58.2153C51.1429 59.7925 50.8067 61.483 50.8067 63.1902C50.8067 64.8974 51.1429 66.5878 51.7962 68.1651C52.4495 69.7423 53.4071 71.1754 54.6143 72.3826L63.8066 63.1902L72.999 53.9978Z" fill="#0051FD"/>',
    '<rect x="54.6152" y="72.3826" width="26" height="120" transform="rotate(-45 54.6152 72.3826)" fill="#0051FD"/>',
    '<path d="M156.438 155.821L139.468 157.235L157.853 138.85L156.438 155.821Z" fill="#0051FD"/>',
  ]},
  // NE — flies in from top-right
  { dx: 0.707, dy: -0.707, elements: [
    '<path d="M300.071 72.6987C301.278 71.4916 302.236 70.0585 302.889 68.4812C303.543 66.904 303.879 65.2135 303.879 63.5064C303.879 61.7992 303.543 60.1087 302.889 58.5315C302.236 56.9542 301.278 55.5211 300.071 54.314C298.864 53.1068 297.431 52.1492 295.854 51.4959C294.277 50.8426 292.586 50.5064 290.879 50.5064C289.172 50.5064 287.481 50.8426 285.904 51.4959C284.327 52.1492 282.894 53.1068 281.687 54.314L290.879 63.5064L300.071 72.6987Z" fill="#0051FD"/>',
    '<rect x="281.686" y="54.314" width="26" height="120" transform="rotate(45 281.686 54.314)" fill="#0051FD"/>',
    '<path d="M198.247 156.137L196.833 139.167L215.218 157.552L198.247 156.137Z" fill="#0051FD"/>',
  ]},
  // SW — flies in from bottom-left
  { dx: -0.707, dy: 0.707, elements: [
    '<path d="M53.999 282.002C52.7919 283.21 51.8343 284.643 51.181 286.22C50.5277 287.797 50.1914 289.488 50.1914 291.195C50.1914 292.902 50.5277 294.592 51.181 296.17C51.8343 297.747 52.7919 299.18 53.999 300.387C55.2062 301.594 56.6393 302.552 58.2165 303.205C59.7938 303.859 61.4842 304.195 63.1914 304.195C64.8986 304.195 66.5891 303.859 68.1663 303.205C69.7435 302.552 71.1766 301.594 72.3838 300.387L63.1914 291.195L53.999 282.002Z" fill="#0051FD"/>',
    '<rect x="72.3848" y="300.387" width="26" height="120" transform="rotate(-135 72.3848 300.387)" fill="#0051FD"/>',
    '<path d="M155.823 198.564L157.237 215.534L138.853 197.15L155.823 198.564Z" fill="#0051FD"/>',
  ]},
];

// How far off-screen each arm starts (in SVG coordinate units)
const FLY_DISTANCE = 200;
// Total assembly duration before spin begins
const ASSEMBLY_DURATION_S = 0.8;
// Stagger between each arm
const STAGGER_S = 0.08;

// Full cycle: fly-in → hold → fly-out → pause → repeat
const HOLD_S = 2.5;       // How long assembled logo stays before flying out
const PAUSE_S = 0.4;      // Pause between fly-out finishing and next fly-in
const FLYOUT_DURATION_S = 0.5;

/**
 * Logo that repeatedly assembles (arms fly in), holds with a spin,
 * then disassembles (arms fly out) and repeats.
 */
function AssemblingLogo() {
  const rotate = useMotionValue(0);
  const scale = useMotionValue(1);
  const rafRef = useRef<number>(0);
  // cycle increments to trigger re-animation: even = fly-in, odd = fly-out
  const [cycle, setCycle] = useState(0);
  const isAssembled = cycle % 2 === 1;

  // Timing: fly-in → wait → flip to fly-out → wait → flip to fly-in
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (cycle % 2 === 0) {
      // Currently flying IN — wait for assembly + hold, then trigger fly-out
      const assemblyMs = (ASSEMBLY_DURATION_S + STAGGER_S * ARMS.length) * 1000;
      timer = setTimeout(() => setCycle((c) => c + 1), assemblyMs + HOLD_S * 1000);
    } else {
      // Currently flying OUT — wait for disassembly + pause, then trigger fly-in
      const flyoutMs = (FLYOUT_DURATION_S + STAGGER_S * ARMS.length) * 1000;
      timer = setTimeout(() => setCycle((c) => c + 1), flyoutMs + PAUSE_S * 1000);
    }
    return () => clearTimeout(timer);
  }, [cycle]);

  // Continuous gentle spin
  useEffect(() => {
    let prevTime = performance.now();

    const tick = (now: number) => {
      const dt = (now - prevTime) / 1000;
      prevTime = now;

      rotate.set(rotate.get() + 60 * dt);

      const t = now / 1000;
      const breath = 1 + 0.04 * Math.sin(t * 1.3);
      scale.set(breath);

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div style={{ rotate, scale }}>
      <svg
        viewBox="0 0 355 354"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-16 h-16"
      >
        {ARMS.map((arm, i) => (
          <motion.g
            key={i}
            initial={{
              x: arm.dx * FLY_DISTANCE,
              y: arm.dy * FLY_DISTANCE,
              opacity: 0,
            }}
            animate={
              isAssembled
                ? {
                    x: arm.dx * FLY_DISTANCE,
                    y: arm.dy * FLY_DISTANCE,
                    opacity: 0,
                  }
                : {
                    x: 0,
                    y: 0,
                    opacity: 1,
                  }
            }
            transition={{
              duration: isAssembled ? FLYOUT_DURATION_S : ASSEMBLY_DURATION_S,
              delay: i * STAGGER_S,
              ease: isAssembled
                ? [0.7, 0, 0.84, 0]  // easeIn — accelerates outward
                : [0.16, 1, 0.3, 1], // expoOut — fast approach, gentle landing
            }}
            dangerouslySetInnerHTML={{ __html: arm.elements.join('') }}
          />
        ))}
      </svg>
    </motion.div>
  );
}

interface ProvisioningScreenProps {
  /** Called when the provisioning API has resolved AND minimum display time elapsed */
  onComplete: () => void;
  /** Whether the backend call has finished (resolved or rejected) */
  isReady: boolean;
}

export function ProvisioningScreen({ onComplete, isReady }: ProvisioningScreenProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [hasMinTime, setHasMinTime] = useState(false);

  // Cycle through messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((i) => (i + 1) % PROVISIONING_MESSAGES.length);
    }, MESSAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Minimum display time — show at least 3s so it doesn't flash
  useEffect(() => {
    const timer = setTimeout(() => setHasMinTime(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // When both API ready + min time elapsed → navigate
  const shouldComplete = isReady && hasMinTime;
  const handleComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    if (shouldComplete) {
      // Small delay for the exit animation
      const timer = setTimeout(handleComplete, 600);
      return () => clearTimeout(timer);
    }
  }, [shouldComplete, handleComplete]);

  const currentMessage = PROVISIONING_MESSAGES[messageIndex];

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Subtle radial gradient backdrop */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-background to-background" />

      <div className="relative flex flex-col items-center gap-8">
        {/* Logo — arms fly in from compass directions, then spin */}
        <AssemblingLogo />

        {/* Rotating messages — Apple Hello style */}
        <div className="h-10 flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={messageIndex}
              className="text-lg font-medium text-foreground/80 whitespace-nowrap"
              style={{ fontFamily: "'IBM Plex Sans Thai', system-ui, sans-serif" }}
              initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -20, filter: 'blur(8px)' }}
              transition={{ duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
            >
              {currentMessage}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Thin progress bar — indeterminate */}
        <div className="w-48 h-0.5 rounded-full bg-muted overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-primary/60"
            animate={{ x: ['-100%', '100%'] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            style={{ width: '40%' }}
          />
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Dev-only standalone preview — renders the provisioning screen
 * with a fake 8-second timer so you can see the full animation
 * without going through the registration flow.
 *
 * Route: /register/provisioning-preview
 */
export function ProvisioningScreenPreview() {
  const [isReady, setIsReady] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Simulate backend taking 8 seconds
    const timer = setTimeout(() => setIsReady(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  if (done) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background">
        <p className="text-lg text-foreground">Provisioning complete! (this would navigate to dashboard)</p>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <ProvisioningScreen isReady={isReady} onComplete={() => setDone(true)} />
    </AnimatePresence>
  );
}
